import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { Branding } from '../../../../React/Common/API/BrandingAPI'
import { BroadworksSSOConfigDTO } from '../../../../NonReact/Types/Types'
import { useBroadworksSSOConfig } from '../../../../React/Common/Hooks/useBroadworksSSOConfig'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { configureFullURLForLocalDevelopment } from '../../../../React/Common/Utils/LocalUtils'
import { useFormContext, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { LoginFormType } from '../../../Types/Types'
import { BROADWORKS_USER_BASIC_REGEX, EMAIL_REGEX } from '../../../../NonReact/constants/Regexes'
import { USERNAME_FIELD_NAME } from '../LoginForm/Constants/FieldNames'
import { RoundedButton } from '../../Atoms/RoundedButton/RoundedButton'
import { StateSetter } from '../../../../React/Common/Types/Types'
import { attemptBroadworksSSOLogin } from './BroadworksSSOUtils'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { Authentication, SignInWith } from '../../../../../i18n'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { displayError } from '../../../../React/Common/Utils/Messages'

type Props = {
    branding: Nullable<Branding>
    bwSSOEnabled: boolean
    isBroadworksSignInMode: boolean
    setBroadworksSSORegEx: StateSetter<Nullable<RegExp>>
    isDisabled?: boolean
}

/**
 * Broadworks SSO component.
 *
 * @param branding - The branding.
 * @param bwSSOEnabled - Whether Broadworks SSO is enabled.
 * @param isBroadworksSignInMode - Whether Broadworks sign in mode is enabled.
 * @param setBroadworksSSORegEx - The Broadworks SSO regular expression setter.
 */
export const BroadworksSSO: FC<Props> = ({
    branding,
    bwSSOEnabled,
    isBroadworksSignInMode,
    setBroadworksSSORegEx,
    isDisabled = false
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { watch, trigger }: UseFormMethods<LoginFormType> = useFormContext()

    const broadworksSSOConfig: Nullable<BroadworksSSOConfigDTO> = useBroadworksSSOConfig()

    const broadworksLogoURL: string = useMemo(
        (): string =>
            configureFullURLForLocalDevelopment(branding?.BrandingConfig?.imagery?.broadworksLogo ?? EMPTY_STRING),
        [branding?.BrandingConfig?.imagery?.broadworksLogo]
    )

    const usernameValue: string = watch(USERNAME_FIELD_NAME)

    const usernameRegex: Nullable<RegExp> = useMemo((): Nullable<RegExp> => {
        if (isVarNotEmpty(broadworksSSOConfig)) {
            if (broadworksSSOConfig.requireFullyQualifiedEmail) {
                return EMAIL_REGEX
            }

            return BROADWORKS_USER_BASIC_REGEX
        }

        return null
    }, [JSON.stringify(broadworksSSOConfig)])

    const isUsernameValid: boolean = useMemo((): boolean => {
        if (usernameRegex) {
            return isVarNotEmpty(usernameValue) && usernameRegex.test(usernameValue)
        }
        return false
    }, [usernameValue, usernameRegex])

    const onClick = (): void => {
        setBroadworksSSORegEx(usernameRegex)
        setTimeout((): void => {
            trigger(USERNAME_FIELD_NAME as keyof LoginFormType)
        })

        if (isUsernameValid) {
            attemptBroadworksSSOLogin(usernameValue, broadworksSSOConfig).catch(displayError)
        }
    }

    return bwSSOEnabled || isVarNotEmpty(broadworksSSOConfig) ? (
        <div className='router-broadworks-sso'>
            <RoundedButton disabled={isDisabled} className='rsiw-sso-button' onClick={onClick}>
                <div className='rsiwsb-logo-container'>
                    <img className='rsiwsb-logo' src={broadworksLogoURL} alt='Broadworks logo' />
                </div>
                <span>
                    {isBroadworksSignInMode
                        ? language.get(Authentication.LoginForm_LandingPage_NextButton)
                        : branding?.BrandingConfig?.languages?.broadworksName}
                </span>
            </RoundedButton>
            {isBroadworksSignInMode ? (
                <RoundedButton
                    className='rsiw-sso-button'
                    onClick={(): void => {
                        setBroadworksSSORegEx(undefined)
                    }}
                >
                    <span>{language.get(SignInWith.SignInWith_OtherOptions)}</span>
                </RoundedButton>
            ) : (
                <></>
            )}
        </div>
    ) : (
        <></>
    )
}
